/**
 * WCAG 2.x contrast-ratio math — T3.10 (root-app-multi-tenant plan, Phase 3).
 *
 * Pure, framework-free functions so the contrast guard in
 * `tenant-theme.service.ts` is trivially unit-testable independent of
 * Angular's injector / DOM. Implements the standard relative-luminance +
 * contrast-ratio formulas from
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance /
 * https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio — the same math browsers'
 * own a11y devtools use.
 */

/** WCAG AA threshold for normal text / UI-sized elements (ui-design.md §6.6). */
export const WCAG_AA_CONTRAST_THRESHOLD = 4.5;

/**
 * Parses a hex color string (`#RGB`, `#RRGGBB`, with or without the `#`)
 * into `[r, g, b]` (0-255 each). Returns `null` for anything that isn't a
 * well-formed hex color — callers treat that as "untrusted input, don't
 * apply it" rather than throwing, since these values come from
 * organization-supplied data.
 */
export function parseHexColor(input: string | null | undefined): [number, number, number] | null {
    if (!input) {
        return null;
    }
    const hex = input.trim().replace(/^#/, '');

    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        const r = hex[0] + hex[0];
        const g = hex[1] + hex[1];
        const b = hex[2] + hex[2];
        return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
    }

    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
        return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16),
        ];
    }

    return null;
}

/** Converts a single 0-255 sRGB channel to its linearized value per the WCAG formula. */
function linearizeChannel(channel255: number): number {
    const c = channel255 / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Relative luminance (0-1) of an `[r, g, b]` (0-255 each) color, per WCAG 2.x. */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
    const rLin = linearizeChannel(r);
    const gLin = linearizeChannel(g);
    const bLin = linearizeChannel(b);
    return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

/**
 * Contrast ratio (1-21) between two hex colors, per WCAG 2.x:
 * `(L1 + 0.05) / (L2 + 0.05)` where L1 is the lighter of the two relative
 * luminances. Returns `null` if either color fails to parse.
 */
export function contrastRatio(
    hexA: string | null | undefined,
    hexB: string | null | undefined
): number | null {
    const rgbA = parseHexColor(hexA);
    const rgbB = parseHexColor(hexB);
    if (!rgbA || !rgbB) {
        return null;
    }

    const lumA = relativeLuminance(rgbA);
    const lumB = relativeLuminance(rgbB);
    const lighter = Math.max(lumA, lumB);
    const darker = Math.min(lumA, lumB);

    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * True when `hex` meets WCAG AA contrast (>= threshold, default 4.5:1)
 * against EVERY color in `backgrounds`. An unparseable `hex` never meets the
 * guard (fails closed — malformed org-supplied color data falls back to the
 * platform default, same as a too-low-contrast one).
 */
export function meetsContrastAgainstAll(
    hex: string | null | undefined,
    backgrounds: readonly string[],
    threshold: number = WCAG_AA_CONTRAST_THRESHOLD
): boolean {
    if (!parseHexColor(hex)) {
        return false;
    }
    return backgrounds.every((bg) => {
        const ratio = contrastRatio(hex, bg);
        return ratio !== null && ratio >= threshold;
    });
}
